import { ReactNode, useMemo } from 'react';
import { Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import DashboardLeadsTable from 'components/widgets/Dashboard/DashboardLeadsTable';
import { usePaginate } from 'hooks/usePaginate';
import { PaginatedProject } from 'types';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { PAGINATED_PROJECT_LIMIT } from 'utils/constants';

const containerStyle = {
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  gap: 'padding_16',
};

const emptyPlaceHolderStyle = {
  justifyContent: 'center',
  paddingTop: 'padding_24',
};

type TProps = {
  collectionName: string;
  collectionItems: PaginatedProject[];
  totalProjectCount: number;
  isLoading: boolean;
  emptyPlaceholder: ReactNode;
};

const CollectionDetailsBasic = ({
  collectionName,
  collectionItems,
  totalProjectCount,
  isLoading,
  emptyPlaceholder,
}: TProps) => {
  const { nextPage, previousPage, page: currentPage, setPageNumber } = usePaginate();

  const totalPages = useMemo((): number => {
    return totalProjectCount
      ? Math.ceil(totalProjectCount / PAGINATED_PROJECT_LIMIT) || 1
      : 1;
  }, [totalProjectCount]);

  const projectCountLabel = useMemo(() => {
    if (totalProjectCount === 1) {
      return `${totalProjectCount} project`;
    }

    return `${totalProjectCount} projects`;
  }, [totalProjectCount]);

  return (
    <FlexColumn sx={containerStyle}>
      <Text.H3>{collectionName}</Text.H3>
      <Text.LargeBold>{projectCountLabel}</Text.LargeBold>
      {isLoading || collectionItems.length > 0 ? (
        <DashboardLeadsTable
          items={collectionItems}
          leadContext={{
            groupId: collectionName,
            groupType: ProjectGroupTypes.COLLECTION,
          }}
          isLoading={isLoading}
          footer={
            <Pagination
              currentPage={currentPage}
              isLoading={isLoading}
              setPage={(pageNumber) => setPageNumber(pageNumber)}
              totalPages={totalPages}
              onNext={nextPage}
              onPrev={previousPage}
            />
          }
        />
      ) : (
        <FlexRow sx={emptyPlaceHolderStyle}>{emptyPlaceholder}</FlexRow>
      )}
    </FlexColumn>
  );
};

export default CollectionDetailsBasic;
